import { errorToast, toast } from "@/v2-ui/toast"
import { useLayerForkMutation } from "@/v2-backoffice-client/layer/fork/layer.fork.mutation"
import { useLayerUpdateMutation } from "@/v2-backoffice-client/layer/update/layer.update.mutation"
import { LAYER_COUNTS_QUERY_NAME } from "@/v2-backoffice-client/layer/counts/layer.counts.constants"
import { LAYER_AUTHORING_STATUSES_QUERY_NAME } from "@/v2-backoffice-client/layer/authoring/layer.authoring.constants"
import { LAYER_QUERY_NAME } from "@/v2-backoffice-client/layer/layer.constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import {
  setLayerAiHandoff,
  type LayerAiChatTurn,
  type LayerAiMode,
} from "@/v2-backoffice-client/layer/ai/layer.ai.handoff"
import type { LayerAiForkProposal } from "@/v2-backoffice-client/layer/ai/layer.ai.types"

export type UseLayerAiForkInput = {
  /** The layer being forked. */
  featureCode: string
  /** The running conversation, handed over to the fork's assistant. */
  mode: LayerAiMode | null
  turns: LayerAiChatTurn[]
  /**
   * Called once the fork exists, BEFORE navigating to it: the parent resets
   * its suggestion/fork state here so nothing tries to update this subtree
   * after the sheet swap unmounts it.
   */
  onForked(): void
}

export type LayerAiForkApplyInput = {
  proposal: LayerAiForkProposal
  /** Blob replacements to land on the fork right after it is created. */
  blobUpdates: Record<string, unknown>
}

/**
 * The assistant's fork-and-apply flow as ONE domain operation: create the
 * fork (copies the source's full setup), overwrite the forked blobs with the
 * assistant's replacements, stash the conversation for the fork's assistant
 * (opening the fork's sheet unmounts this one), then jump there so the
 * operator continues in the same context.
 */
export function useLayerAiFork(input: UseLayerAiForkInput) {
  const { featureCode, mode, turns, onForked } = input

  const opener = useAppViewSheetOpener()

  const [forkLayer, forkState] = useLayerForkMutation({
    refetchQueries: [
      LAYER_QUERY_NAME,
      LAYER_COUNTS_QUERY_NAME,
      LAYER_AUTHORING_STATUSES_QUERY_NAME,
    ],
  })
  errorToast(forkState.error, { title: "Failed to fork the layer" })

  const [updateForkedLayer, updateForkedState] = useLayerUpdateMutation()
  errorToast(updateForkedState.error, {
    title: "The fork was created, but applying the suggestions failed",
  })

  /** Returns the fork's feature code, or null when the fork didn't happen. */
  async function forkAndApply(apply: LayerAiForkApplyInput) {
    const { proposal, blobUpdates } = apply

    const forkResult = await forkLayer({
      variables: {
        input: {
          SourceFeatureCode: featureCode,
          FeatureCode: proposal.FeatureCode.trim(),
          Name: proposal.Name.trim(),
        },
      },
    })
    const forked = forkResult.data?.forkLayer
    if (!forked) return null

    if (Object.keys(blobUpdates).length > 0 || proposal.Description) {
      const updateResult = await updateForkedLayer({
        variables: {
          input: {
            FeatureCode: forked.FeatureCode,
            Description: proposal.Description?.trim() || undefined,
            ...blobUpdates,
          },
        },
      })
      // errorPolicy is "all" app-wide, so a failed apply RESOLVES with
      // errors instead of throwing. The fork exists but still carries the
      // source's blobs — stop here (the wired error toast explains) instead
      // of navigating with a success story.
      if (updateResult.errors?.length || !updateResult.data?.updateLayer) {
        return null
      }
    }

    // Clean up FIRST: opening the fork's sheet unmounts this subtree, and
    // the parent's state reset must not race that swap.
    onForked()

    toast({
      title: `Forked into ${forked.FeatureCode}.`,
      description:
        "The assistant's setup was applied to the fork — it's in the review queue, opening now.",
    })
    setLayerAiHandoff({
      targetFeatureCode: forked.FeatureCode,
      mode: mode ?? "wizard",
      turns: [
        ...turns,
        {
          role: "assistant",
          content: `Forked \`${featureCode}\` into \`${forked.FeatureCode}\` with the accepted setup carried over — we're now working on the fork.`,
        },
      ],
    })
    opener.open("layer", forked.FeatureCode)
    return forked.FeatureCode
  }

  return {
    forkAndApply,
    isForking: forkState.loading || updateForkedState.loading,
  }
}
